import Icon from '@mdi/react';
import { mdiClipboardMultiple, mdiLinkVariantRemove } from '@mdi/js';
import React, { useState, useEffect, useMemo } from 'react';
import { OnChangeValue, ActionMeta, MultiValue } from 'react-select';
import { IEntityManagerProps, IEntityConfig } from './entity-manager.types';
import { EntitySearchInput } from './entity-search-input';
import { BaseButton, Tooltip, BaseSelect, ISelectOption } from '../../base';
import * as S from './entity-manager.styles';

const PAGE_SIZE = 5;

export const EntityManager: React.FunctionComponent<IEntityManagerProps> = (props) => {
    const { messageId, entityIdsByType, entityConfigs, onEntityAdd, onBulkEntitiesAdd, onEntityRemove } = props;
    
    const [selectedEntityType, setSelectedEntityType] = useState<string>(
        entityConfigs[0]?.entityType || ''
    );
    const [entities, setEntities] = useState<any[]>([]);
    const [loadingEntities, setLoadingEntities] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const currentConfig = useMemo(() => {
        return entityConfigs.find(config => config.entityType === selectedEntityType);
    }, [selectedEntityType, entityConfigs]);

    const currentEntityIds = useMemo(() => {
        return entityIdsByType[selectedEntityType] || [];
    }, [entityIdsByType, selectedEntityType]);

    useEffect(() => {
        const fetchEntities = async () => {
            if (!currentConfig || !currentEntityIds || currentEntityIds.length === 0) {
                setEntities([]);
                setLoadingEntities(false);
                return;
            }

            setLoadingEntities(true);
            try {
                const fetchedEntities = await currentConfig.fetchEntitiesFn(currentEntityIds);
                setEntities(fetchedEntities);
            } catch (error) {
                console.error('Failed to fetch entities:', error);
                setEntities([]);
            } finally {
                setLoadingEntities(false);
            }
        };

        fetchEntities();
    }, [currentEntityIds, currentConfig]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedEntityType]);

    const paginatedEntities = useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        return entities.slice(startIndex, startIndex + PAGE_SIZE);
    }, [entities, currentPage]);

    const totalPages = Math.ceil(entities.length / PAGE_SIZE);

    const entityTypeOptions: ISelectOption[] = useMemo(() => {
        return entityConfigs.map(config => ({
            value: config.entityType,
            label: config.displayNamePlural
        }));
    }, [entityConfigs]);

    const selectedEntityOption = useMemo(() => {
        return entityTypeOptions.find(opt => opt.value === selectedEntityType);
    }, [entityTypeOptions, selectedEntityType]);

    const handleEntityTypeChange = (
        newValue: ISelectOption | MultiValue<ISelectOption>,
        actionMeta: ActionMeta<ISelectOption>
    ) => {
        if (newValue && !Array.isArray(newValue) && 'value' in newValue) {
            setSelectedEntityType(newValue.value as string);
        }
    };

    const handleEntitySelected = async (entityId: string) => {
        await onEntityAdd(selectedEntityType, entityId);
        setCurrentPage(1);
    };

    const handleBulkEntitiesSelected = async (entityIds: string[]) => {
        if (onBulkEntitiesAdd) {
            try {
                await onBulkEntitiesAdd(selectedEntityType, entityIds);
            } catch (error) {
                console.error('Failed to link entities:', error);
            }
        } else {
            for (const entityId of entityIds) {
                try {
                    await onEntityAdd(selectedEntityType, entityId);
                } catch (error) {
                    console.error(`Failed to link entity ${entityId}:`, error);
                }
            }
        }
        setCurrentPage(1);
    };

    const handleCopyAllEntityIds = () => {
        if (!currentConfig) return;
        const allEntityIds = entities
            .map(entity => currentConfig.getEntityId(entity))
            .filter(id => id)
            .join('\n');
        navigator.clipboard.writeText(allEntityIds);
    };

    const handleRemoveEntity = async (entity: any) => {
        if (!currentConfig) return;
        const entityId = currentConfig.getEntityId(entity);
        if (!entityId) return;
        
        await onEntityRemove(selectedEntityType, entityId);
        
        if (paginatedEntities.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const renderSummaryMetrics = () => {
        if (!currentConfig?.summaryMetrics || currentConfig.summaryMetrics.length === 0) {
            return null;
        }

        return (
            <S.SummarySection>
                {currentConfig.summaryMetrics.map((metric, index) => (
                    <S.SummaryLabel key={index}>
                        <S.SummaryLabelTitle>{metric.label}:</S.SummaryLabelTitle>
                        <S.SummaryLabelValue>{metric.calculate(entities)}</S.SummaryLabelValue>
                    </S.SummaryLabel>
                ))}
            </S.SummarySection>
        );
    };

    if (!currentConfig) {
        return (
            <S.Container>
                <S.TabContent>
                    <S.EmptyMessage>No entity configurations available</S.EmptyMessage>
                </S.TabContent>
            </S.Container>
        );
    }

    return (
        <S.Container>
            <S.TabContent>
                {entityConfigs.length > 1 && (
                    <S.EntityTypeSelectorContainer>
                        <S.EntityTypeSelectorLabel>Entity Type</S.EntityTypeSelectorLabel>
                        <BaseSelect
                            options={entityTypeOptions}
                            value={selectedEntityOption}
                            onChange={handleEntityTypeChange}
                            required={true}
                        />
                    </S.EntityTypeSelectorContainer>
                )}

                <S.SearchSection>
                    <EntitySearchInput
                        entityConfig={currentConfig}
                        onEntitySelected={handleEntitySelected}
                        onBulkEntitiesSelected={handleBulkEntitiesSelected}
                        disabled={false}
                    />
                </S.SearchSection>

                {loadingEntities ? (
                    <S.LoadingMessage>Loading {currentConfig.displayNamePlural.toLowerCase()}...</S.LoadingMessage>
                ) : entities.length === 0 ? (
                    <S.EmptyMessage>
                        No {currentConfig.displayNamePlural.toLowerCase()} linked to this message
                    </S.EmptyMessage>
                ) : (
                    <>
                        {renderSummaryMetrics()}

                        <S.Table>
                            <S.TableHead>
                                <S.TableRow>
                                    {currentConfig.columns.map(column => (
                                        <S.TableHeader key={column.key} style={{ width: column.width }}>
                                            {column.label}
                                        </S.TableHeader>
                                    ))}
                                    {currentConfig.actions.length > 0 && (
                                        <S.TableHeader>Actions</S.TableHeader>
                                    )}
                                </S.TableRow>
                            </S.TableHead>
                            <S.TableBody>
                                {paginatedEntities.map((entity, index) => {
                                    const entityId = currentConfig.getEntityId(entity);
                                    return (
                                        <S.TableRow key={entityId || index}>
                                            {currentConfig.columns.map(column => (
                                                <S.TableCell key={column.key}>
                                                    {column.render 
                                                        ? column.render(entity)
                                                        : column.accessor
                                                            ? column.accessor(entity)
                                                            : '-'}
                                                </S.TableCell>
                                            ))}
                                            {currentConfig.actions.length > 0 && (
                                                <S.TableCell>
                                                    <S.ActionsContainer className="actions-container">
                                                        {currentConfig.actions.map(action => {
                                                            if (action.isVisible && !action.isVisible(entity)) {
                                                                return null;
                                                            }

                                                            return (
                                                                <Tooltip 
                                                                    key={action.key}
                                                                    title={<span>{action.label}</span>} 
                                                                    direction="top"
                                                                >
                                                                    <S.ActionButton 
                                                                        onClick={() => action.handler(entity)}
                                                                    >
                                                                        <Icon path={action.icon} size={0.7} />
                                                                    </S.ActionButton>
                                                                </Tooltip>
                                                            );
                                                        })}
                                                        <Tooltip title={<span>Unlink {currentConfig.displayName}</span>} direction="top">
                                                            <S.ActionButton onClick={() => handleRemoveEntity(entity)}>
                                                                <Icon path={mdiLinkVariantRemove} size={0.7} />
                                                            </S.ActionButton>
                                                        </Tooltip>
                                                    </S.ActionsContainer>
                                                </S.TableCell>
                                            )}
                                        </S.TableRow>
                                    );
                                })}
                            </S.TableBody>
                        </S.Table>

                        <S.TableFooter>
                            {totalPages > 1 && (
                                <S.PaginationContainer>
                                    <S.PaginationInfo>
                                        Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, entities.length)} of {entities.length}
                                    </S.PaginationInfo>
                                    <S.PaginationControls>
                                        <BaseButton
                                            variant="outlined"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                        >
                                            Previous
                                        </BaseButton>
                                        <S.PageInfo>
                                            {currentPage} of {totalPages}
                                        </S.PageInfo>
                                        <BaseButton
                                            variant="outlined"
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                        >
                                            Next
                                        </BaseButton>
                                    </S.PaginationControls>
                                </S.PaginationContainer>
                            )}
                            <Tooltip title={<span>Copy All {currentConfig.displayName} IDs</span>} direction="top">
                                <S.CopyAllButton onClick={handleCopyAllEntityIds}>
                                    <Icon path={mdiClipboardMultiple} size={0.65} />
                                </S.CopyAllButton>
                            </Tooltip>
                        </S.TableFooter>
                    </>
                )}
            </S.TabContent>
        </S.Container>
    );
};

